import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, Users, PartyPopper, Award, Ticket } from "lucide-react";

/**
 * "You saw the pitches. Now come experience the Business Shower."
 * Full campaign section anchored between the hero and the get-involved forms.
 * Renders after pitch week completes (phase >= 3).
 */
export default function PostPitchExperience() {
  const [tab, setTab] = useState("both"); // 'both' | 'friday' | 'saturday'

  return (
    <section
      id="experience-the-shower"
      data-testid="postpitch-section"
      className="relative bg-gradient-to-b from-emerald-950 via-emerald-900 to-emerald-950 text-white overflow-hidden"
    >
      {/* Subtle grain / halo */}
      <div aria-hidden className="absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(245,208,110,0.15),transparent_55%),radial-gradient(circle_at_85%_85%,rgba(225,29,72,0.18),transparent_50%)]" />
      <div aria-hidden className="absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100'><filter id='n'><feTurbulence baseFrequency='0.85'/></filter><rect width='100' height='100' filter='url(%23n)' opacity='0.6'/></svg>\")" }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-16 sm:py-20">
        {/* Kicker */}
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-amber-300" />
          <span className="text-[11px] uppercase tracking-[0.28em] text-amber-300 font-[Outfit] font-bold">The pitches were just the beginning</span>
        </div>

        {/* Headline */}
        <h2 className="font-[Cormorant_Garamond] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white max-w-4xl">
          You saw the pitches.
          <br />
          <span className="text-amber-300 italic">Now come experience the Business Shower.</span>
        </h2>

        <p className="mt-6 max-w-3xl font-[Outfit] text-base sm:text-lg text-emerald-50/85 leading-relaxed">
          Our 2026 Business Shower honorees have worked, prepared, refined their businesses, and shared their vision with all of us on those live pitch nights. Now we&apos;re inviting <b>the community</b> to show up, celebrate them, connect with resources, and be part of the next step in their journey.
        </p>

        {/* Two-day tab selector */}
        <div className="mt-10 inline-flex rounded-full bg-white/10 backdrop-blur border border-white/20 p-1 gap-1" data-testid="postpitch-tabs">
          {[
            { k: "both", label: "Two-Day Experience" },
            { k: "friday", label: "Fri · July 17" },
            { k: "saturday", label: "Sat · July 18" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`postpitch-tab-${t.k}`}
              className={`px-4 py-2 rounded-full font-[Outfit] text-xs font-semibold uppercase tracking-wider transition-all ${tab === t.k ? "bg-amber-300 text-emerald-950" : "text-emerald-50/70 hover:text-white"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Days grid */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Friday VIP */}
          {(tab === "both" || tab === "friday") && (
            <article data-testid="postpitch-day-friday" className="rounded-2xl bg-emerald-800/60 backdrop-blur border border-emerald-700/50 p-7 sm:p-8 hover:border-amber-300/70 transition-colors">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-amber-300 font-[Outfit] font-bold">
                <Users className="w-3.5 h-3.5" />
                Day 1 · Friday, July 17 · 6:00 PM ET
              </div>
              <h3 className="mt-3 font-[Cormorant_Garamond] text-3xl text-white leading-tight">
                VIP Event
              </h3>
              <div className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-amber-300/15 border border-amber-300/40 px-3 py-1 text-[11px] font-[Outfit] font-semibold text-amber-200">
                ~75 guest experience
              </div>
              <ul className="mt-5 space-y-2 font-[Outfit] text-sm text-emerald-50/90">
                <li className="flex gap-2"><span className="text-amber-300 shrink-0">•</span> Dinner &amp; intentional networking</li>
                <li className="flex gap-2"><span className="text-amber-300 shrink-0">•</span> Sponsor tables &amp; resource connections</li>
                <li className="flex gap-2"><span className="text-amber-300 shrink-0">•</span> Funding conversations</li>
                <li className="flex gap-2"><span className="text-amber-300 shrink-0">•</span> The room where small business owners meet the people who can support their growth</li>
              </ul>
              <Button
                asChild
                data-testid="postpitch-friday-cta"
                className="mt-6 rounded-full bg-amber-300 hover:bg-amber-400 text-emerald-950 font-[Outfit] font-bold px-5 h-10"
              >
                <a href="/tickets#friday">Attend the VIP Event →</a>
              </Button>
            </article>
          )}

          {/* Saturday Main */}
          {(tab === "both" || tab === "saturday") && (
            <article data-testid="postpitch-day-saturday" className="rounded-2xl bg-gradient-to-br from-amber-300 to-amber-400 text-emerald-950 p-7 sm:p-8 hover:from-amber-200 hover:to-amber-300 transition-colors border border-amber-500/40">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-emerald-900 font-[Outfit] font-bold">
                <PartyPopper className="w-3.5 h-3.5" />
                Day 2 · Saturday, July 18 · 12:00 PM ET
              </div>
              <h3 className="mt-3 font-[Cormorant_Garamond] text-3xl text-emerald-950 leading-tight">
                Main Business Shower
              </h3>
              <div className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-emerald-950/15 border border-emerald-950/25 px-3 py-1 text-[11px] font-[Outfit] font-semibold text-emerald-950">
                <Award className="w-3 h-3" /> The award reveal moment
              </div>
              <ul className="mt-5 space-y-2 font-[Outfit] text-sm text-emerald-950/85">
                <li className="flex gap-2"><span className="text-rose-600 shrink-0 font-bold">•</span> The full Business Shower celebration</li>
                <li className="flex gap-2"><span className="text-rose-600 shrink-0 font-bold">•</span> Honor the finalists &amp; pour into them with resources</li>
                <li className="flex gap-2"><span className="text-rose-600 shrink-0 font-bold">•</span> Community together — family-friendly</li>
                <li className="flex gap-2"><span className="text-rose-600 shrink-0 font-bold">•</span> The big award reveal</li>
              </ul>
              <Button
                asChild
                data-testid="postpitch-saturday-cta"
                className="mt-6 rounded-full bg-emerald-950 hover:bg-emerald-900 text-white font-[Outfit] font-bold px-5 h-10"
              >
                <a href="/tickets#saturday">Attend the Business Shower →</a>
              </Button>
            </article>
          )}
        </div>

        {/* Sponsor tables block */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-0 rounded-2xl overflow-hidden border border-white/20 bg-white/5 backdrop-blur">
          <div className="md:col-span-2 p-6 sm:p-8">
            <div className="text-[11px] uppercase tracking-[0.22em] text-rose-300 font-[Outfit] font-bold">Sponsor tables · Both days</div>
            <h3 className="mt-2 font-[Cormorant_Garamond] text-2xl sm:text-3xl text-white leading-tight">
              Put your brand in the room.
            </h3>
            <p className="mt-2 font-[Outfit] text-sm text-emerald-50/80 leading-relaxed">
              Sponsor tables are open for both Friday and Saturday to <b>small businesses, nonprofits, resource partners, and community organizations</b> that want to represent their brand, connect with attendees, and support small businesses in a meaningful way.
            </p>
          </div>
          <div className="p-6 sm:p-8 flex items-center justify-center bg-rose-500/10 border-l border-white/10">
            <Button
              asChild
              data-testid="postpitch-sponsor-cta"
              className="w-full rounded-full bg-rose-500 hover:bg-rose-600 text-white font-[Outfit] font-bold px-5 h-11"
            >
              <a href="/sponsor">Sponsor a Table →</a>
            </Button>
          </div>
        </div>

        {/* Primary Two-Day CTA */}
        <div className="mt-10 sm:mt-14 rounded-3xl bg-gradient-to-r from-rose-500 via-rose-600 to-rose-500 p-8 sm:p-10 text-center shadow-2xl">
          <blockquote className="font-[Cormorant_Garamond] text-2xl sm:text-3xl text-white italic leading-tight max-w-3xl mx-auto">
            &ldquo;Buy your two-day ticket, put your brand in the room, and come celebrate what these businesses are building.&rdquo;
          </blockquote>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              asChild
              size="lg"
              data-testid="postpitch-twoday-cta"
              className="rounded-full bg-white text-rose-600 hover:bg-emerald-50 font-[Outfit] font-bold text-base px-8 h-14"
            >
              <a href="/tickets"><Ticket className="w-4 h-4 mr-2" />Buy Your Two-Day Ticket</a>
            </Button>
          </div>
          <p className="mt-5 font-[Outfit] text-sm text-rose-50/95 italic">
            Come for the connections. Come for the celebration. Come for the reveal.
          </p>
        </div>
      </div>
    </section>
  );
}
